// MediaViewer — full-screen modal for viewing an image or PDF attachment.
//
// Opens over the conversation reading pane when a message thumbnail is tapped.
// Images render centered and scaled to fit the viewport (object-contain, never
// cropped); PDFs render in a browser-native <iframe> viewer with an "Open in new
// tab" link for cross-origin PDFs that refuse framing. Other file kinds fall
// through to an "Open in new tab" chip.
//
// Closes on backdrop click, the × button, or Escape. Mirrors the overlay pattern
// used by CdpSessionDetail (fixed inset-0 z-50 + backdrop), not a portal — keeps
// it consistent with the rest of the hub surfaces.
import React, { useEffect } from "react";

const IMG_EXT = /\.(png|jpe?g|gif|webp|svg|avif|bmp|ico)(\?|#|$)/i;
const MD_EXT = /\.(md|markdown)(\?|#|$)/i;
const VIDEO_EXT = /\.(mov|mp4|webm|ogv|m4v)(\?|#|$)/i;

export function classifyAttachment(type, url) {
  const t = typeof type === "string" ? type.toLowerCase() : "";
  if (t.startsWith("image/")) return "image";
  if (t.startsWith("video/")) return "video";
  if (t === "application/pdf" || t.includes("pdf")) return "pdf";
  if (t === "text/markdown" || t.includes("markdown")) return "markdown";
  if (IMG_EXT.test(url || "")) return "image";
  if (VIDEO_EXT.test(url || "")) return "video";
  if (/\.pdf(\?|#|$)/i.test(url || "")) return "pdf";
  if (MD_EXT.test(url || "")) return "markdown";
  return "file";
}

/**
 * @param {object} props
 * @param {{ url: string, name?: string, type?: string }} props.attachment
 *   The attachment to view. When null/missing url, renders nothing.
 * @param {() => void} props.onClose
 */
export default function MediaViewer({ attachment, onClose }) {
  const att = attachment || {};
  const url = att.url;
  const name = att.name || "attachment";

  // Escape closes; lock body scroll while the viewer is open.
  useEffect(() => {
    if (!url) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [url, onClose]);

  if (!url) return null;

  const kind = classifyAttachment(att.type, url);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      data-testid="media-viewer"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Top bar */}
      <div className="relative z-10 flex items-center gap-3 px-4 py-3 text-white">
        <span className="min-w-0 truncate text-sm font-medium">{name}</span>
        {(kind === "pdf" || kind === "file" || kind === "video") && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-md border border-white/30 px-2.5 py-1 text-xs font-medium hover:bg-white/10"
            data-testid="media-viewer-open-tab"
          >
            Open in new tab
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          className="ml-auto flex h-8 w-8 items-center justify-center rounded-full hover:bg-white/10"
          aria-label="Close viewer"
          data-testid="media-viewer-close"
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center p-4">
        {kind === "image" ? (
          <img
            src={url}
            alt={name}
            className="max-h-full max-w-full rounded-lg object-contain shadow-2xl"
            data-testid="media-viewer-img"
          />
        ) : kind === "video" ? (
          <video
            src={url}
            controls
            autoPlay
            className="max-h-full max-w-full rounded-lg bg-black shadow-2xl"
            data-testid="media-viewer-video"
          >
            Your browser does not support embedded video.{" "}
            <a href={url} target="_blank" rel="noreferrer" className="underline">
              Open it here
            </a>
            .
          </video>
        ) : kind === "pdf" ? (
          <iframe
            src={url}
            title={name}
            className="h-full w-full max-w-5xl rounded-lg bg-white shadow-2xl"
            data-testid="media-viewer-pdf"
          />
        ) : (
          <div className="flex flex-col items-center gap-3 text-white">
            <svg
              className="h-12 w-12 opacity-80"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="rounded-md border border-white/30 px-3 py-1.5 text-sm font-medium hover:bg-white/10"
            >
              Open {name}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}